import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Button, Flex, Input, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import { useDeferredValue, useState } from "react";
import { api, unwrap, type Schemas } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { ExportButton } from "@/components/list/ExportButton";
import { useListPage } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { BinCountModal, BinMoveModal } from "../inventory/Inventory";
import { ConditionTag, conditionOptions, conditions, type Condition } from "../inventory/conditions";
import { NoWms, useWmsWarehouse } from "./common";

type BinRow = Schemas["BinRow"];

const KINDS = ["", "storage", "receiving", "returns", "packing", "outbound", "damaged", "quarantine", "variance"] as const;
type Kind = (typeof KINDS)[number];
const SORTS = ["location", "received", "expiry"] as const;
type Sort = (typeof SORTS)[number];
const AGES = ["", "30", "60", "90", "180"] as const;
type Age = (typeof AGES)[number];
const EXPIRING = ["", "0", "30", "60", "90"] as const;
type Expiring = (typeof EXPIRING)[number];
const CONDITIONS = ["", ...conditions] as const;

/**
 * Stok per lokasi (WMS): every bin of the active warehouse - location and
 * zone, SKU, lot and expiry, condition, units and what open tasks hold, and
 * the receipt date its oldest units came in with. Counts and moves happen
 * here per bin, as in the stock detail.
 */
export function BinStock() {
  const { t, tDynamic, formatNumber, formatDate } = useI18n();
  const { can, me } = useAuth();
  const tz = me?.workspace?.timezone;
  const { current, loading } = useWmsWarehouse();
  const [search, setSearch] = useUrlFilter<string>("q", "");
  const [locSearch, setLocSearch] = useUrlFilter<string>("location", "");
  const [condition, setCondition] = useUrlFilter<"" | Condition>("condition", "", CONDITIONS);
  const [kind, setKind] = useUrlFilter<Kind>("kind", "", KINDS);
  const [age, setAge] = useUrlFilter<Age>("olderThan", "", AGES);
  const [expiring, setExpiring] = useUrlFilter<Expiring>("expiring", "", EXPIRING);
  const [sort, setSort] = useUrlFilter<Sort>("sort", "location", SORTS);
  const q = useDeferredValue(search.trim());
  const location = useDeferredValue(locSearch.trim().toUpperCase());
  const page = useListPage();
  const [counting, setCounting] = useState<BinRow | null>(null);
  const [moving, setMoving] = useState<BinRow | null>(null);
  const manage = can("inventory.manage");
  const move = can("warehouse.operate") || manage;

  const filters = {
    warehouseId: current?.id ?? "",
    ...(q ? { q } : {}),
    ...(location ? { location } : {}),
    ...(condition ? { condition } : {}),
    ...(kind ? { kind } : {}),
    ...(age ? { olderThan: Number(age) } : {}),
    ...(expiring ? { expiring: Number(expiring) } : {}),
    ...(sort !== "location" ? { sort } : {}),
  };
  const list = useQuery({
    queryKey: ["inventory", "bins", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/inventory/bins", { params: { query: { ...filters, ...page.query } } })),
    enabled: !!current,
    placeholderData: keepPreviousData,
  });

  if (!loading && !current) return <NoWms />;
  const sku = (b: BinRow) => ({ id: b.skuId, code: b.skuCode, name: b.skuName });
  const today = new Date().toISOString().slice(0, 10);

  return (
    <>
      <Flex justify="space-between" align="baseline" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("bins.title")}
        </Typography.Title>
        <Typography.Text type="secondary">{t("bins.subtitle", { name: current?.name ?? "" })}</Typography.Text>
      </Flex>

      <Flex gap={12} wrap style={{ marginBottom: 16 }} align="center">
        <Input.Search allowClear placeholder={t("bins.search")} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 280 }} />
        <Input.Search
          allowClear
          placeholder={t("inventory.locationSearch")}
          value={locSearch}
          onChange={(e) => setLocSearch(e.target.value)}
          className="z-num"
          style={{ width: 200 }}
        />
        <Select<"" | Condition>
          value={condition}
          onChange={setCondition}
          style={{ width: 150 }}
          options={[{ value: "", label: t("bins.allConditions") }, ...conditionOptions(t)]}
        />
        <Select<Kind>
          value={kind}
          onChange={setKind}
          style={{ width: 170 }}
          options={KINDS.map((k) => ({ value: k, label: k ? t(`wms.loc.kind.${k}`) : t("bins.allKinds") }))}
        />
        <Select<Age>
          value={age}
          onChange={setAge}
          style={{ width: 170 }}
          options={AGES.map((a) => ({ value: a, label: a ? t("bins.olderThan", { n: a }) : t("bins.anyAge") }))}
        />
        <Select<Expiring>
          value={expiring}
          onChange={setExpiring}
          style={{ width: 190 }}
          options={EXPIRING.map((e) => ({ value: e, label: e === "" ? t("bins.anyExpiry") : e === "0" ? t("bins.expired") : t("bins.expiringIn", { n: e }) }))}
        />
        <Select<Sort>
          value={sort}
          onChange={setSort}
          style={{ width: 200 }}
          options={SORTS.map((s) => ({ value: s, label: t(`bins.sort.${s}`) }))}
        />
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/inventory/bins/export"
          query={filters}
          filename={`${t("bins.title")} ${current?.name ?? ""}`.trim()}
          columns={[
            { key: "locationCode", label: t("inventory.bin.location") },
            { key: "zone", label: t("wms.loc.zone") },
            { key: "skuCode", label: t("catalog.code") },
            { key: "skuName", label: t("common.name") },
            { key: "lotNo", label: t("inventory.bin.lot") },
            { key: "expiresOn", label: t("inventory.bin.expires"), kind: "date" },
            { key: "condition", label: t("inventory.condition"), values: Object.fromEntries(conditions.map((c) => [c, t(`inventory.condition.${c}`)])) },
            { key: "quantity", label: t("wms.loc.units"), kind: "number" },
            { key: "reserved", label: t("inventory.bin.reserved"), kind: "number" },
            { key: "free", label: t("bins.free"), kind: "number" },
            { key: "receivedAt", label: t("bins.receivedAt"), kind: "date" },
            { key: "ageDays", label: t("bins.age"), kind: "number" },
          ]}
        />
      </Flex>

      <Table<BinRow>
        rowKey="id"
        size="middle"
        loading={list.isFetching && !list.data}
        dataSource={list.data?.items ?? []}
        pagination={page.pagination(list.data)}
        scroll={{ x: 1300 }}
        locale={{ emptyText: t("bins.empty") }}
        columns={[
          {
            title: t("inventory.bin.location"),
            dataIndex: "locationCode",
            width: 170,
            fixed: "left",
            render: (v: string, b) => (
              <Space size={4} wrap>
                <Typography.Text strong className="z-num">
                  {v}
                </Typography.Text>
                {b.locationKind !== "storage" && <Tag>{tDynamic(`wms.loc.kind.${b.locationKind}`, "wms.loc.kind.storage")}</Tag>}
                {b.counting && <Tag color="warning">{t("wms.count.frozen")}</Tag>}
              </Space>
            ),
          },
          { title: t("wms.loc.zone"), dataIndex: "zone", width: 80, render: (v: string) => (v ? <Tag className="z-num">{v}</Tag> : "—") },
          { title: t("catalog.code"), dataIndex: "skuCode", width: 170, render: (v: string) => <span className="z-num">{v}</span> },
          { title: t("common.name"), dataIndex: "skuName", width: 220, ellipsis: true },
          { title: t("inventory.bin.lot"), dataIndex: "lotNo", width: 120, render: (v: string | null) => <span className="z-num">{v ?? "—"}</span> },
          {
            title: t("inventory.bin.expires"),
            dataIndex: "expiresOn",
            width: 120,
            render: (v: string | null) =>
              v ? (
                <Typography.Text type={v < today ? "danger" : undefined} className="z-num">
                  {formatDate(v, tz)}
                </Typography.Text>
              ) : (
                "—"
              ),
          },
          { title: t("inventory.condition"), dataIndex: "condition", width: 110, render: (v: Condition) => <ConditionTag condition={v} /> },
          { title: t("wms.loc.units"), dataIndex: "quantity", width: 90, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
          {
            title: t("inventory.bin.reserved"),
            dataIndex: "reserved",
            width: 110,
            align: "right",
            render: (v: number) => <span className="z-num">{v ? formatNumber(v) : "—"}</span>,
          },
          {
            title: t("bins.free"),
            dataIndex: "free",
            width: 90,
            align: "right",
            render: (v: number) => (
              <Typography.Text strong className="z-num">
                {formatNumber(v)}
              </Typography.Text>
            ),
          },
          {
            title: (
              <Tooltip title={t("bins.receivedHelp")}>
                <span>{t("bins.receivedAt")}</span>
              </Tooltip>
            ),
            dataIndex: "receivedAt",
            width: 130,
            render: (v: string) => <span className="z-num">{formatDate(v, tz)}</span>,
          },
          {
            title: t("bins.age"),
            dataIndex: "ageDays",
            width: 90,
            align: "right",
            render: (v: number) => (
              <Typography.Text type={v >= 90 ? "warning" : undefined} className="z-num">
                {t("bins.days", { n: formatNumber(v) })}
              </Typography.Text>
            ),
          },
          ...(move
            ? [
                {
                  width: 150,
                  fixed: "right" as const,
                  // Counted and variance locations change through counts only.
                  render: (_: unknown, b: BinRow) =>
                    !b.counting &&
                    b.locationKind !== "variance" && (
                      <Space>
                        {manage && (
                          <Button size="small" onClick={() => setCounting(b)}>
                            {t("inventory.bin.adjust")}
                          </Button>
                        )}
                        {b.free > 0 && (
                          <Button size="small" onClick={() => setMoving(b)}>
                            {t("inventory.bin.move")}
                          </Button>
                        )}
                      </Space>
                    ),
                },
              ]
            : []),
        ]}
      />
      {counting && <BinCountModal sku={sku(counting)} warehouseId={current?.id ?? ""} bin={counting} onClose={() => setCounting(null)} />}
      {moving && <BinMoveModal sku={sku(moving)} warehouseId={current?.id ?? ""} bin={moving} onClose={() => setMoving(null)} />}
    </>
  );
}
